/* eslint-disable no-underscore-dangle */
import { useEffect, useId, useState } from 'react';

import classNames from 'classnames';
import PropTypes from 'prop-types';

import { BorderedBox } from '#client/pages/EducationStudio/components/BorderedBox';
import { formatSeconds } from '#client/utils/time';

import { useStyle } from './useStyles';

export const WistiaVideo = ({
  id, isPlaying, onPlay, onPause, onEnd, isBordered, setVideoDuration
}) => {
  const uniqueId = useId();
  const classes = useStyle();
  const [video, setVideo] = useState();

  // Video player properties
  useEffect(() => {
    const wistiaVideo = document.createElement('script');
    wistiaVideo.src = `//fast.wistia.com/embed/medias/${id}.jsonp`;
    wistiaVideo.async = true;

    document.body.appendChild(wistiaVideo);
    return () => {
      document.body.removeChild(wistiaVideo);
    };
  }, [id]);

  useEffect(() => {
    const embedInitConfig = {
      id: uniqueId,
      onReady: (_video) => {
        setVideo(_video);
        if (setVideoDuration) {
          setVideoDuration(formatSeconds(_video.duration()));
        }
      }
    };

    window._wq = window._wq || [];
    window._wq.push(embedInitConfig);

    return () => {
      window._wq.push({ revoke: embedInitConfig });
    };
  }, [uniqueId, setVideoDuration]);

  useEffect(() => {
    if (!video) return;

    video.bind('play', onPlay);
    video.bind('pause', onPause);
    video.bind('end', onEnd);
    // eslint-disable-next-line consistent-return
    return () => {
      video.unbind('play', onPlay);
      video.unbind('pause', onPause);
      video.unbind('end', onEnd);
    };
  }, [onEnd, onPause, onPlay, video]);

  // when user presses the Watch Video button play the video
  // if it already ended, restart it
  useEffect(() => {
    /* global Wistia */
    /* eslint no-undef: "error" */
    try {
      const _video = Wistia?.api(uniqueId);
      if (isPlaying && _video && _video.state() !== 'playing') {
        if (_video.state() === 'ended') {
          _video.time(0);
        }
        _video.play();
      }
    } catch {
      /* no-op */
    }
  }, [uniqueId, isPlaying]);

  return (
    <BorderedBox
      id={uniqueId}
      className={classNames(
        `wistia_embed wistia_async_${id} videoFoam=true`,
        isBordered ? classes.videoPlayer : classes.videoContainer
      )}
    />
  );
};

WistiaVideo.propTypes = {
  id: PropTypes.string.isRequired,
  isPlaying: PropTypes.bool,
  onPlay: PropTypes.func,
  onPause: PropTypes.func,
  onEnd: PropTypes.func,
  isBordered: PropTypes.bool,
  setVideoDuration: PropTypes.func
};

WistiaVideo.defaultProps = {
  isPlaying: false,
  isBordered: false,
  onPlay: () => {},
  onPause: () => {},
  onEnd: () => {},
  setVideoDuration: () => {}
};
